import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Key, Mail, User, Clock, CheckCircle, XCircle, Send } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const PasswordResetManager = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, denied, completed, all

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/password-reset/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load reset requests:', error);
      console.warn('Password reset requests not available yet');
      setLoading(false);
    }
  };

  const handleGenerateToken = async (requestId) => {
    if (!window.confirm('Generate and send a password reset link to this user?')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/password-reset/generate-token/${requestId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Reset link sent to user');
      loadRequests();
    } catch (error) {
      console.error('Failed to generate token:', error);
      toast.error(error.response?.data?.error || 'Failed to generate reset link');
    }
  };

  const handleDenyRequest = async (requestId) => {
    if (!window.confirm('Deny this password reset request?')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${API_URL}/password-reset/deny/${requestId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Request denied');
      loadRequests();
    } catch (error) {
      console.error('Failed to deny request:', error);
      toast.error('Failed to deny request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending' },
      approved: { color: 'bg-blue-100 text-blue-800', icon: Send, text: 'Link Sent' },
      denied: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Denied' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Completed' },
      expired: { color: 'bg-gray-100 text-gray-800', icon: Clock, text: 'Expired' },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon size={12} className="mr-1" />
        {badge.text}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      groomer: 'bg-blue-100 text-blue-800',
      client: 'bg-green-100 text-green-800',
    };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-800'}`}>
        {role}
      </span>
    );
  };

  const filteredRequests = requests.filter(req => {
    if (filter === 'all') return true;
    return req.status === filter;
  });

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Key className="text-primary-600" size={24} />
          <h2 className="text-2xl font-bold">Password Reset Requests</h2>
        </div>
        <button
          onClick={loadRequests}
          className="btn btn-secondary text-sm"
        >
          Refresh
        </button>
      </div>

      <p className="text-gray-600 mb-6">
        Manage password reset requests from users. Generate secure reset links and send them via email.
      </p>

      {/* Filter Tabs */}
      <div className="flex space-x-2 mb-6 border-b border-gray-200">
        {['pending', 'approved', 'completed', 'denied', 'all'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              filter === status
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
              {status === 'all' ? requests.length : requests.filter(r => r.status === status).length}
            </span>
          </button>
        ))}
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Key size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No {filter !== 'all' ? filter : ''} password reset requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div
              key={request.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="flex items-center space-x-2">
                      <User size={16} className="text-gray-400" />
                      <span className="font-semibold text-gray-900">
                        {request.first_name} {request.last_name}
                      </span>
                    </div>
                    {getRoleBadge(request.role)}
                    {getStatusBadge(request.status)}
                  </div>

                  <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                    <Mail size={14} />
                    <span>{request.email}</span>
                  </div>

                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Clock size={12} />
                      <span>Requested: {new Date(request.created_at).toLocaleString()}</span>
                    </div>
                    {request.expires_at && (
                      <div className="flex items-center space-x-1">
                        <Clock size={12} />
                        <span>
                          Expires: {new Date(request.expires_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {request.completed_at && (
                    <div className="mt-2 text-xs text-gray-500">
                      Completed: {new Date(request.completed_at).toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Actions */}
                {request.status === 'pending' && (
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleGenerateToken(request.id)}
                      className="btn btn-primary text-sm flex items-center space-x-1"
                      title="Generate and send reset link"
                    >
                      <Send size={14} />
                      <span>Send Reset Link</span>
                    </button>
                    <button
                      onClick={() => handleDenyRequest(request.id)}
                      className="btn btn-secondary text-sm flex items-center space-x-1"
                      title="Deny request"
                    >
                      <XCircle size={14} />
                      <span>Deny</span>
                    </button>
                  </div>
                )}

                {request.status === 'approved' && new Date(request.expires_at) > new Date() && (
                  <div className="ml-4 text-sm text-blue-600 font-medium">
                    Link sent - waiting for user
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">How It Works:</h3>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>User requests password reset from login page</li>
          <li>Request appears here for admin review</li>
          <li>Admin clicks "Send Reset Link" to generate secure token</li>
          <li>User receives email with reset link (expires in 1 hour)</li>
          <li>User clicks link and sets new password</li>
          <li>Request marked as completed</li>
        </ol>
        <p className="text-xs text-blue-700 mt-3">
          <strong>Security Note:</strong> Passwords are encrypted and cannot be viewed. Reset links are single-use and expire after 1 hour.
        </p>
      </div>
    </div>
  );
};

export default PasswordResetManager;
